import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { DASHBOARD_TILES, type Dashboard } from '@tailoring-crm/shared';
import { formatCurrency, formatNumber } from '../../lib/format.js';
import { cn } from '@tailoring-crm/ui';
import { TILE_VISUALS, TONE_CLASSES, type DashboardTileKey, type Visual } from './visuals.js';

/**
 * The morning figures, each one a door.
 *
 * Every tile is a `<Link>` — not a card with a number and a chevron that does nothing. The rule
 * task 26 sets is that a number without a route to the records behind it is decoration, and the
 * way to keep a rule like that is to make it structural: the destination is declared beside the
 * figure in `DASHBOARD_TILES`, in the shared package, so a tile cannot be added without one.
 *
 * Money tiles disappear entirely when the API answers `money: null`, which it does for anybody
 * the permission matrix does not let near rupees. They are not greyed out and they do not read
 * zero; a figure somebody may not know is a figure that is not on their screen.
 *
 * Each tile wears the icon and tone `TILE_VISUALS` gives it, and keeps that colour even at zero —
 * a brand-new shop should still look alive. Overdue is the one exception, below.
 */

/** How each tile reads its number out of the payload, and how that number is written. */
const READERS: Readonly<Record<DashboardTileKey, (dashboard: Dashboard) => string | null>> =
  Object.freeze({
    dueToday: (d) => formatNumber(d.counts.dueToday),
    overdue: (d) => formatNumber(d.counts.overdue),
    ready: (d) => formatNumber(d.counts.readyForCollection),
    inProgress: (d) => formatNumber(d.counts.inProgress),
    bookedToday: (d) => formatNumber(d.counts.ordersBookedToday),
    outstanding: (d) => (d.money ? formatCurrency(d.money.outstandingPaise) : null),
    collectedToday: (d) => (d.money ? formatCurrency(d.money.collectedTodayPaise) : null),
    unsettledWages: (d) => (d.money ? formatCurrency(d.money.unsettledWagesPaise) : null),
  });

function isTileKey(key: string): key is DashboardTileKey {
  return Object.hasOwn(TILE_VISUALS, key);
}

/**
 * The icon and tone a tile wears today.
 *
 * Overdue is red only while something is actually overdue. A red tile reading "0" is an alarm
 * about nothing, and an alarm that is always on stops being read — so at zero it goes slate.
 */
function visualFor(key: DashboardTileKey, dashboard: Dashboard): Visual {
  const visual = TILE_VISUALS[key];
  if (key === 'overdue' && dashboard.counts.overdue === 0) return { ...visual, tone: 'slate' };
  return visual;
}

export function DashboardTiles({ dashboard }: { dashboard: Dashboard }): ReactElement {
  const { t } = useTranslation();

  return (
    <ul className="grid grid-cols-2 gap-2 sm:grid-cols-4">
      {DASHBOARD_TILES.map((tile) => {
        if (!isTileKey(tile.key)) return null;
        const value = READERS[tile.key](dashboard);
        if (value === null) return null;

        const { icon: Icon, tone } = visualFor(tile.key, dashboard);
        const classes = TONE_CLASSES[tone];
        // The number itself only ever turns red to say something is late.
        const alarming = tile.key === 'overdue' && tone === 'red';

        return (
          <li key={tile.key}>
            <Link
              to={tile.to}
              data-tone={tone}
              className={cn(
                'flex h-full flex-col gap-2 rounded-lg border p-3 transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                classes.tint,
              )}
            >
              <span className="flex items-center gap-2">
                <span
                  className={cn(
                    'flex size-8 shrink-0 items-center justify-center rounded-full',
                    classes.chip,
                  )}
                >
                  <Icon className="size-4" aria-hidden="true" />
                </span>
                <span className="text-xs text-muted-foreground">
                  {t(`dashboard.tile.${tile.key}`)}
                </span>
              </span>
              <span
                className={cn(
                  'text-2xl font-semibold tabular-nums',
                  alarming ? classes.text : 'text-foreground',
                )}
              >
                {value}
              </span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
